import type { ReportTransactionItem } from '@eriko/shared-types';
import { DataTable } from '../components/ui/data-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { transactionColumns } from './columns';

interface ReportsTransactionsSectionProps {
  transactions: ReportTransactionItem[];
  periodLabel: string;
  loading: boolean;
}

export function ReportsTransactionsSection({
  transactions,
  periodLabel,
  loading,
}: ReportsTransactionsSectionProps) {
  // TanStack Table's `useReactTable` is flagged by the React Hooks lint plugin
  // as "incompatible" because it conditionally calls internal hooks. Safe here.
  // eslint-disable-next-line react-hooks/incompatible-library
  const txTable = useReactTable({
    data: transactions,
    columns: transactionColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <section className="bg-surface-0/60 border border-surface-300/70 rounded-[6px] px-8 py-7">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[2px] text-surface-600 mb-2">
            LARGEST TRANSACTIONS — {periodLabel.toUpperCase()}
          </div>
          <h2 className="font-serif text-[22px] tracking-tight text-surface-900">
            The ten <span className="italic font-normal">most significant</span> entries
          </h2>
        </div>
        {loading ? (
          <span
            aria-hidden
            className="h-3 w-20 bg-surface-200/80 rounded-sm animate-pulse"
          />
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-[2px] text-surface-600">
            SHOWING {transactions.length} OF {transactions.length}
          </span>
        )}
      </div>
      {loading ? (
        <TransactionsSkeleton />
      ) : transactions.length === 0 ? (
        <div className="py-12 text-center">
          <p className="font-serif italic text-[16px] text-surface-500">
            Nothing to report — yet.
          </p>
        </div>
      ) : (
        <DataTable table={txTable} />
      )}
    </section>
  );
}

function TransactionsSkeleton({ rows = 10 }: { rows?: number }) {
  return (
    <div className="animate-pulse" aria-hidden>
      <div className="flex items-center gap-4 px-3 py-2 border-b border-surface-200/70">
        <div className="h-2.5 bg-surface-200/70 rounded-sm" style={{ width: 90 }} />
        <div className="flex-1 h-2.5 bg-surface-200/70 rounded-sm" />
        <div className="h-2.5 bg-surface-200/70 rounded-sm" style={{ width: 140 }} />
        <div className="h-2.5 bg-surface-200/70 rounded-sm" style={{ width: 110 }} />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-3 py-3 border-b border-surface-200/50 last:border-b-0"
        >
          <div className="h-3 bg-surface-200/80 rounded-sm" style={{ width: 90 }} />
          <div
            className="flex-1 h-3 bg-surface-200/80 rounded-sm"
            style={{ maxWidth: `${60 + ((i * 7) % 30)}%` }}
          />
          <div className="h-5 bg-surface-200/80 rounded-md" style={{ width: 140 }} />
          <div className="h-3 bg-surface-200/80 rounded-sm" style={{ width: 110 }} />
        </div>
      ))}
    </div>
  );
}
